import type {
  BlockRoomRequestDto,
  CreateHousekeepingTaskRequestDto,
  ReleaseRoomBlockRequestDto,
  ReportMaintenanceIssueRequestDto,
  UpdateHousekeepingTaskRequestDto,
  UpdateMaintenanceReportRequestDto,
  UpdateRoomStateRequestDto,
} from "./contracts/dtos.ts";
import {
  validateBlockRoomRequest,
  validateCreateHousekeepingTaskRequest,
  validateReleaseRoomBlockRequest,
  validateReportMaintenanceIssueRequest,
  validateUpdateHousekeepingTaskRequest,
  validateUpdateMaintenanceReportRequest,
  validateUpdateRoomStateRequest,
} from "./contracts/schemas.ts";
import { applyRoomState, deriveRoomStatus, isRoomAssignable, isRoomReadyForCheckin } from "./roomState.ts";
import type {
  HousekeepingAuditEntry,
  HousekeepingContext,
  HousekeepingRealtimeEnvelope,
  HousekeepingTask,
  MaintenanceReport,
  RoomBlock,
  RoomStateUpdateResult,
} from "./types.ts";
import type { Room, RoomAvailabilityStatus } from "../types/room.ts";

type IdFactory = (prefix: string) => string;

function hasErrors(fieldErrors: Record<string, string>) {
  return Object.values(fieldErrors).some(Boolean);
}

function buildAudit(
  idFactory: IdFactory,
  now: string,
  input: Omit<HousekeepingAuditEntry, "id" | "timestamp">
): HousekeepingAuditEntry {
  return {
    ...input,
    id: idFactory("hk-audit"),
    timestamp: now,
  };
}

function buildRealtimeEnvelope(
  idFactory: IdFactory,
  now: string,
  room: Room,
  input: Omit<HousekeepingRealtimeEnvelope, "id" | "createdAt" | "revision">
): HousekeepingRealtimeEnvelope {
  return {
    ...input,
    id: idFactory("hk-rt"),
    createdAt: now,
    revision: room.revision,
  };
}

function getRoomOrFail(roomId: string, context: HousekeepingContext) {
  return context.rooms.find((room) => room.id === roomId) ?? null;
}

function buildConflict(room: Room) {
  return {
    ok: false as const,
    code: "ROOM_STATE_CONFLICT" as const,
    message: `Room ${room.number} has been updated by another workflow. Refresh the latest room state and retry.`,
    room,
  };
}

function updateRoomVersion(
  room: Room,
  now: string,
  actor: string | undefined,
  patch: Partial<Pick<Room, "housekeepingStatus" | "availabilityStatus" | "activeBlockId" | "activeMaintenanceId">>
) {
  return applyRoomState({
    ...room,
    revision: room.revision + 1,
    lastUpdatedAt: now,
    lastUpdatedBy: actor,
  }, patch);
}

function releaseAvailability(room: Room): RoomAvailabilityStatus {
  if (room.activeMaintenanceId) return "out_of_order";
  if (room.activeBlockId) return "blocked";
  return "available";
}

export function createHousekeepingTask(
  request: CreateHousekeepingTaskRequestDto,
  context: HousekeepingContext,
  dependencies: { idFactory: IdFactory }
) {
  const fieldErrors = validateCreateHousekeepingTaskRequest(request);
  if (hasErrors(fieldErrors)) {
    return { ok: false as const, message: "Task request is invalid.", fieldErrors };
  }

  const room = getRoomOrFail(request.roomId, context);
  if (!room) return { ok: false as const, message: "Room not found." };

  const task: HousekeepingTask = {
    id: request.taskId,
    roomId: request.roomId,
    taskType: request.taskType,
    status: request.assignedTo ? "assigned" : "open",
    priority: request.priority,
    assignedTo: request.assignedTo?.trim() || undefined,
    note: request.note?.trim() || undefined,
    dueAt: request.dueAt,
    actor: request.actor,
    createdAt: context.now,
    updatedAt: context.now,
  };

  return {
    ok: true as const,
    task,
    auditEntry: buildAudit(dependencies.idFactory, context.now, {
      roomId: room.id,
      action: "task_created",
      actor: request.actor,
      message: `Housekeeping task ${task.taskType} created for room ${room.number}.`,
      metadata: {
        taskId: task.id,
        priority: task.priority,
      },
    }),
    realtimeEnvelope: buildRealtimeEnvelope(dependencies.idFactory, context.now, room, {
      roomId: room.id,
      actor: request.actor,
      type: "task.created",
      payload: {
        taskId: task.id,
        taskType: task.taskType,
        taskStatus: task.status,
      },
    }),
  };
}

export function updateRoomState(
  request: UpdateRoomStateRequestDto,
  context: HousekeepingContext,
  dependencies: { idFactory: IdFactory }
) {
  const fieldErrors = validateUpdateRoomStateRequest(request);
  if (hasErrors(fieldErrors)) {
    return { ok: false as const, message: "Room update request is invalid.", fieldErrors };
  }

  const room = getRoomOrFail(request.roomId, context);
  if (!room) return { ok: false as const, message: "Room not found." };
  if (room.revision !== request.expectedRevision) return buildConflict(room);

  const nextRoom = updateRoomVersion(room, context.now, request.actor, {
    housekeepingStatus: request.housekeepingStatus,
    availabilityStatus: request.availabilityStatus,
  });

  return {
    ok: true as const,
    ...({
      room: nextRoom,
      auditEntry: buildAudit(dependencies.idFactory, context.now, {
        roomId: room.id,
        action: "room_status_updated",
        actor: request.actor,
        message: `Room ${room.number} updated to ${deriveRoomStatus(nextRoom)}.`,
        metadata: {
          source: request.source,
          status: nextRoom.status,
          revision: nextRoom.revision.toString(),
        },
      }),
      realtimeEnvelope: buildRealtimeEnvelope(dependencies.idFactory, context.now, nextRoom, {
        roomId: nextRoom.id,
        actor: request.actor,
        type: "room.updated",
        payload: {
          status: nextRoom.status,
          housekeepingStatus: nextRoom.housekeepingStatus,
          availabilityStatus: nextRoom.availabilityStatus,
          source: request.source,
        },
      }),
    } satisfies RoomStateUpdateResult),
  };
}

export function updateHousekeepingTask(
  request: UpdateHousekeepingTaskRequestDto,
  context: HousekeepingContext,
  dependencies: { idFactory: IdFactory }
) {
  const fieldErrors = validateUpdateHousekeepingTaskRequest(request);
  if (hasErrors(fieldErrors)) {
    return { ok: false as const, message: "Task update request is invalid.", fieldErrors };
  }

  const room = getRoomOrFail(request.roomId, context);
  if (!room) return { ok: false as const, message: "Room not found." };

  const task = context.tasks.find((entry) => entry.id === request.taskId && entry.roomId === request.roomId);
  if (!task) return { ok: false as const, message: "Task not found." };

  let nextRoom = room;
  if (request.setRoomHousekeepingStatus) {
    if (request.expectedRoomRevision === undefined) {
      return { ok: false as const, message: "Expected room revision is required when task updates the room state." };
    }
    if (room.revision !== request.expectedRoomRevision) return buildConflict(room);
    nextRoom = updateRoomVersion(room, context.now, request.actor, {
      housekeepingStatus: request.setRoomHousekeepingStatus,
      availabilityStatus: room.availabilityStatus === "available" ? "available" : room.availabilityStatus,
    });
  }

  const nextTask: HousekeepingTask = {
    ...task,
    status: request.status,
    assignedTo: request.assignedTo?.trim() || task.assignedTo,
    note: request.note?.trim() || task.note,
    updatedAt: context.now,
    completedAt: request.status === "completed" || request.status === "verified" ? context.now : task.completedAt,
    actor: request.actor ?? task.actor,
  };

  return {
    ok: true as const,
    task: nextTask,
    room: nextRoom,
    auditEntry: buildAudit(dependencies.idFactory, context.now, {
      roomId: room.id,
      action: "task_updated",
      actor: request.actor,
      message: `Task ${task.id} moved to ${request.status} for room ${room.number}.`,
      metadata: {
        taskId: task.id,
        taskStatus: request.status,
        roomStatus: nextRoom.status,
      },
    }),
    realtimeEnvelope: buildRealtimeEnvelope(dependencies.idFactory, context.now, nextRoom, {
      roomId: room.id,
      actor: request.actor,
      type: "task.updated",
      payload: {
        taskId: task.id,
        taskStatus: nextTask.status,
        roomStatus: nextRoom.status,
      },
    }),
  };
}

export function reportMaintenanceIssue(
  request: ReportMaintenanceIssueRequestDto,
  context: HousekeepingContext,
  dependencies: { idFactory: IdFactory }
) {
  const fieldErrors = validateReportMaintenanceIssueRequest(request);
  if (hasErrors(fieldErrors)) {
    return { ok: false as const, message: "Maintenance report is invalid.", fieldErrors };
  }

  const room = getRoomOrFail(request.roomId, context);
  if (!room) return { ok: false as const, message: "Room not found." };
  if (room.revision !== request.expectedRoomRevision) return buildConflict(room);

  const report: MaintenanceReport = {
    id: request.reportId,
    roomId: room.id,
    title: request.title.trim(),
    description: request.description.trim(),
    severity: request.severity,
    status: "reported",
    createdAt: context.now,
    updatedAt: context.now,
    actor: request.actor,
  };

  const nextRoom = updateRoomVersion(room, context.now, request.actor, {
    activeMaintenanceId: report.id,
    availabilityStatus:
      (request.takeRoomOutOfOrder ?? (request.severity === "high" || request.severity === "critical"))
        ? "out_of_order"
        : room.availabilityStatus,
  });

  return {
    ok: true as const,
    report,
    room: nextRoom,
    auditEntry: buildAudit(dependencies.idFactory, context.now, {
      roomId: room.id,
      action: "maintenance_reported",
      actor: request.actor,
      message: `Maintenance issue reported for room ${room.number}.`,
      metadata: {
        reportId: report.id,
        severity: report.severity,
        status: nextRoom.status,
      },
    }),
    realtimeEnvelope: buildRealtimeEnvelope(dependencies.idFactory, context.now, nextRoom, {
      roomId: room.id,
      actor: request.actor,
      type: "maintenance.created",
      payload: {
        reportId: report.id,
        severity: report.severity,
        roomStatus: nextRoom.status,
      },
    }),
  };
}

export function updateMaintenanceReport(
  request: UpdateMaintenanceReportRequestDto,
  context: HousekeepingContext,
  dependencies: { idFactory: IdFactory }
) {
  const fieldErrors = validateUpdateMaintenanceReportRequest(request);
  if (hasErrors(fieldErrors)) {
    return { ok: false as const, message: "Maintenance update is invalid.", fieldErrors };
  }

  const room = getRoomOrFail(request.roomId, context);
  if (!room) return { ok: false as const, message: "Room not found." };

  const report = context.maintenanceReports.find((entry) => entry.id === request.reportId && entry.roomId === request.roomId);
  if (!report) return { ok: false as const, message: "Maintenance report not found." };

  let nextRoom = room;
  const wantsRelease = request.releaseRoomWhenResolved && (request.status === "resolved" || request.status === "closed");
  if (wantsRelease) {
    if (request.expectedRoomRevision === undefined) {
      return { ok: false as const, message: "Expected room revision is required to release the room." };
    }
    if (room.revision !== request.expectedRoomRevision) return buildConflict(room);
    nextRoom = updateRoomVersion(room, context.now, request.actor, {
      activeMaintenanceId: room.activeMaintenanceId === report.id ? undefined : room.activeMaintenanceId,
      availabilityStatus: room.activeMaintenanceId === report.id ? releaseAvailability({ ...room, activeMaintenanceId: undefined }) : room.availabilityStatus,
      housekeepingStatus: request.status === "resolved" || request.status === "closed" ? "dirty" : room.housekeepingStatus,
    });
  }

  const nextReport: MaintenanceReport = {
    ...report,
    status: request.status,
    updatedAt: context.now,
    resolvedAt: request.status === "resolved" || request.status === "closed" ? context.now : report.resolvedAt,
    actor: request.actor ?? report.actor,
    resolutionNote: request.resolutionNote?.trim() || report.resolutionNote,
  };

  return {
    ok: true as const,
    report: nextReport,
    room: nextRoom,
    auditEntry: buildAudit(dependencies.idFactory, context.now, {
      roomId: room.id,
      action: "maintenance_updated",
      actor: request.actor,
      message: `Maintenance report ${report.id} moved to ${request.status}.`,
      metadata: {
        reportId: report.id,
        roomStatus: nextRoom.status,
      },
    }),
    realtimeEnvelope: buildRealtimeEnvelope(dependencies.idFactory, context.now, nextRoom, {
      roomId: room.id,
      actor: request.actor,
      type: "maintenance.updated",
      payload: {
        reportId: report.id,
        reportStatus: nextReport.status,
        roomStatus: nextRoom.status,
      },
    }),
  };
}

export function blockRoom(
  request: BlockRoomRequestDto,
  context: HousekeepingContext,
  dependencies: { idFactory: IdFactory }
) {
  const fieldErrors = validateBlockRoomRequest(request);
  if (hasErrors(fieldErrors)) {
    return { ok: false as const, message: "Room block request is invalid.", fieldErrors };
  }

  const room = getRoomOrFail(request.roomId, context);
  if (!room) return { ok: false as const, message: "Room not found." };
  if (room.revision !== request.expectedRoomRevision) return buildConflict(room);
  if (!isRoomAssignable(room) && room.activeBlockId) {
    return { ok: false as const, message: "Room is already blocked." };
  }

  const block: RoomBlock = {
    id: request.blockId,
    roomId: room.id,
    reason: request.reason.trim(),
    from: request.from,
    until: request.until,
    actor: request.actor,
    note: request.note?.trim() || undefined,
    status: "active",
    createdAt: context.now,
    updatedAt: context.now,
  };

  const nextRoom = updateRoomVersion(room, context.now, request.actor, {
    activeBlockId: block.id,
    availabilityStatus: room.availabilityStatus === "out_of_order" ? "out_of_order" : "blocked",
  });

  return {
    ok: true as const,
    block,
    room: nextRoom,
    auditEntry: buildAudit(dependencies.idFactory, context.now, {
      roomId: room.id,
      action: "room_blocked",
      actor: request.actor,
      message: `Room ${room.number} blocked.`,
      metadata: {
        blockId: block.id,
        reason: block.reason,
      },
    }),
    realtimeEnvelope: buildRealtimeEnvelope(dependencies.idFactory, context.now, nextRoom, {
      roomId: room.id,
      actor: request.actor,
      type: "room.blocked",
      payload: {
        blockId: block.id,
        reason: block.reason,
        roomStatus: nextRoom.status,
      },
    }),
  };
}

export function releaseRoomBlock(
  request: ReleaseRoomBlockRequestDto,
  context: HousekeepingContext,
  dependencies: { idFactory: IdFactory }
) {
  const fieldErrors = validateReleaseRoomBlockRequest(request);
  if (hasErrors(fieldErrors)) {
    return { ok: false as const, message: "Room block release is invalid.", fieldErrors };
  }

  const room = getRoomOrFail(request.roomId, context);
  if (!room) return { ok: false as const, message: "Room not found." };
  if (room.revision !== request.expectedRoomRevision) return buildConflict(room);

  const block = context.roomBlocks.find((entry) => entry.id === request.blockId && entry.roomId === request.roomId);
  if (!block) return { ok: false as const, message: "Room block not found." };
  if (block.status !== "active") return { ok: false as const, message: "Room block is already released." };

  const nextBlock: RoomBlock = {
    ...block,
    status: "released",
    updatedAt: context.now,
    releasedAt: context.now,
    releasedBy: request.actor,
    note: request.note?.trim() || block.note,
  };

  const nextRoom = updateRoomVersion(room, context.now, request.actor, {
    activeBlockId: room.activeBlockId === block.id ? undefined : room.activeBlockId,
    availabilityStatus: room.activeBlockId === block.id ? releaseAvailability({ ...room, activeBlockId: undefined }) : room.availabilityStatus,
  });

  return {
    ok: true as const,
    block: nextBlock,
    room: nextRoom,
    auditEntry: buildAudit(dependencies.idFactory, context.now, {
      roomId: room.id,
      action: "room_block_released",
      actor: request.actor,
      message: `Room block ${block.id} released for room ${room.number}.`,
      metadata: {
        blockId: block.id,
        roomStatus: nextRoom.status,
      },
    }),
    realtimeEnvelope: buildRealtimeEnvelope(dependencies.idFactory, context.now, nextRoom, {
      roomId: room.id,
      actor: request.actor,
      type: "room.block.released",
      payload: {
        blockId: block.id,
        roomStatus: nextRoom.status,
      },
    }),
  };
}

export { isRoomAssignable, isRoomReadyForCheckin };
